import { calculateAdjacencyMap } from 'automation-ui-directed-graph/DirectedNodes';
import { FIRST_NODE_EDGE } from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
const addEdgeIdToNodes = ({
  ancestorNodes,
  fromEdgeId,
  nodeIdToEndMarkerEdges
}) => {
  if (ancestorNodes.size < 1) {
    return nodeIdToEndMarkerEdges;
  }
  let nodeIdToEndEdges = new Map(nodeIdToEndMarkerEdges);
  ancestorNodes.forEach(nodeId => {
    let edges = nodeIdToEndEdges.get(nodeId);
    if (edges) {
      edges = edges.add(fromEdgeId);
    } else {
      edges = new Set([fromEdgeId]);
    }
    nodeIdToEndEdges = nodeIdToEndEdges.set(nodeId, edges);
  });
  return nodeIdToEndEdges;
};
const addNodeIdToAncestors = (nodeId, visitedNodes, fromEdge) => {
  const fromNodeId = fromEdge && fromEdge.getFromNode() || '';
  const parentVisitedNode = visitedNodes.get(fromNodeId);
  let ancestorNodes = new Set();
  if (!parentVisitedNode) {
    ancestorNodes = new Set([nodeId]);
  } else {
    ancestorNodes = new Set(parentVisitedNode.ancestorNodes);
    ancestorNodes.add(nodeId);
  }
  return ancestorNodes;
};

/*
 * This function gets called when we traverse the graph and
 * run across a node we already visisted. This is due to
 * goTo edges. We take the upline errors and ancestors and
 * add it to all the affected endMarkers.
 */
const addAncestorDataToEdgeMarkers = ({
  nodeId,
  fromEdge,
  visitedNodes,
  nodeIdToEndMarkerEdges,
  endMarkerNodes
}) => {
  let mutatedNodeIdToEndMarkerEdges = new Map(nodeIdToEndMarkerEdges);
  let mutatedEndMarkerNodes = new Map(endMarkerNodes);
  if (!fromEdge) {
    return {
      nodeIdToEndMarkerEdges,
      endMarkerNodes
    };
  }
  const fromNodeId = fromEdge.fromNode;
  const fromNodeVisited = visitedNodes.get(fromNodeId);
  const affectedEdges = nodeIdToEndMarkerEdges.get(nodeId);
  if (!fromNodeVisited) return {
    nodeIdToEndMarkerEdges,
    endMarkerNodes
  };
  if (!affectedEdges || affectedEdges.size < 1) {
    return {
      nodeIdToEndMarkerEdges,
      endMarkerNodes
    };
  }
  affectedEdges.forEach(edgeId => {
    const edge = endMarkerNodes.get(edgeId);
    if (!edge) return;
    edge.ancestorNodes = new Set([...edge.ancestorNodes, ...fromNodeVisited.ancestorNodes]);
    mutatedEndMarkerNodes = mutatedEndMarkerNodes.set(edgeId, edge);

    // update our nodeIdToEndMarkerEdges to account for a goTo edge hooking up with a pre-existing branch.
    fromNodeVisited.ancestorNodes.forEach(localNodeId => {
      let baseEffectedEdges = mutatedNodeIdToEndMarkerEdges.get(localNodeId);
      if (baseEffectedEdges) {
        baseEffectedEdges = baseEffectedEdges.add(edgeId);
      } else {
        baseEffectedEdges = new Set([edgeId]);
      }
      mutatedNodeIdToEndMarkerEdges = mutatedNodeIdToEndMarkerEdges.set(localNodeId, baseEffectedEdges);
    });
  });
  return {
    nodeIdToEndMarkerEdges: mutatedNodeIdToEndMarkerEdges,
    endMarkerNodes: mutatedEndMarkerNodes
  };
};
const addEndMarkerForStartNode = endMarkerNodes => {
  return endMarkerNodes.set(FIRST_NODE_EDGE, {
    fromNode: 0,
    ancestorNodes: new Set([])
  });
};
/**
 * Traverses our workflow using DFS on the adjacenyMap to iterate against the children array.
 * Builds a data object that contains the resolved edgeId for every branch and if it has any
 * upline errors that we should reflect in the UI.
 */
const addEndMarkerEdgesByTraversal = ({
  endMarkerNodes,
  startNodeId,
  directedGraph
}) => {
  const adjacencyMap = calculateAdjacencyMap(directedGraph.getEdges());
  const nodeStack = [{
    nodeId: startNodeId
  }]; // make a stack for us to deal with push on our first actionId
  let visitedNodes = new Map([]); // map of nodes we have already traversed with data from our traversal
  let nodeIdToEndMarkerEdges = new Map([]); // map of nodeIds and a list of edges they resolve to.

  while (nodeStack.length) {
    const currNode = nodeStack.pop();
    const nodeId = currNode.nodeId;
    const fromEdge = currNode.edge;
    if (!nodeId) {
      continue;
    }

    // We can end up at a previsouly visted Node if there are goTo edges
    // in the workflow. We need to add our traverse data to the end markers
    // that the visited nodeId resolves to.
    const visitedNode = visitedNodes.get(nodeId);
    if (visitedNode) {
      // grab the history of the "fromNode" and add it to the affected downline branches
      // recomputing the nodeIdToEndMarkerEdges to account for goTo branches going to goTo branches
      const addAncestorResults = addAncestorDataToEdgeMarkers({
        nodeId,
        fromEdge,
        visitedNodes,
        nodeIdToEndMarkerEdges,
        endMarkerNodes
      });
      endMarkerNodes = addAncestorResults.endMarkerNodes;
      nodeIdToEndMarkerEdges = addAncestorResults.nodeIdToEndMarkerEdges;
      continue;
    }
    const ancestorNodes = addNodeIdToAncestors(nodeId, visitedNodes, fromEdge);
    visitedNodes = visitedNodes.set(nodeId, {
      nodeId,
      ancestorNodes
    });
    const nodeEdges = adjacencyMap.get(nodeId) || [];

    // push children onto the stack
    // eslint-disable-next-line no-loop-func
    nodeEdges.forEach(edge => {
      const toNode = edge.getToNode();
      const fromNode = edge.getFromNode();

      // if toNode is null then generate an endMarker for the edge
      if (!toNode) {
        endMarkerNodes = endMarkerNodes.set(edge.edgeId, {
          fromNode,
          ancestorNodes
        });
        nodeIdToEndMarkerEdges = addEdgeIdToNodes({
          ancestorNodes,
          fromEdgeId: edge.edgeId,
          nodeIdToEndMarkerEdges
        });
        return;
      }
      if (edge.isGoToEdge()) {
        const goToNextPath = directedGraph.goToPaths[toNode];
        if (goToNextPath && goToNextPath.toNodeId) {
          const nextNodeIdFromGoTo = goToNextPath.toNodeId;
          nodeStack.push({
            nodeId: nextNodeIdFromGoTo,
            edge
          });
        }
        return;
      }
      nodeStack.push({
        nodeId: toNode,
        edge
      });
      return;
    });
  }
  return endMarkerNodes;
};

/**
 * Generates a data object that contains all of the "edges" that will have end markers
 * along with their ancestor nodes, errors along the branch and what node was the
 * parent of the end marker
 */
export const getAllEndMarkersData = ({
  flow,
  directedGraph
}) => {
  const startNodeId = flow.get('firstActionId');
  let endMarkerNodes = new Map([]);

  // Workflows with no actions should return a single edgeMarker with an error
  if (!startNodeId) {
    endMarkerNodes = addEndMarkerForStartNode(endMarkerNodes);
  } else {
    endMarkerNodes = addEndMarkerEdgesByTraversal({
      endMarkerNodes,
      startNodeId,
      directedGraph
    });
  }
  return endMarkerNodes;
};